import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

const TEAL = '#4DB6AC';

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function prepareComplimentaryByVenue(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = data?.datasets || [];
  const ds = rawDatasets[0];
  if (!ds) {
    return { labels: [], values: [] };
  }

  const rows = rawLabels
    .map((label, i) => ({ label, value: toNum(ds.data?.[i]) }))
    .filter(r => r.value > 0)
    .sort((a, b) => b.value - a.value);

  return {
    labels: rows.map(r => r.label),
    values: rows.map(r => r.value),
  };
}

const ComplimentaryRevenueByVenueChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name || 'Complimentary Revenue by Venue';
  const prepared = useMemo(() => prepareComplimentaryByVenue(data), [data]);

  const plotHeight = useMemo(
    () => Math.min(440, Math.max(200, prepared.labels.length * 32)),
    [prepared.labels.length],
  );

  const option = useMemo(
    () => ({
      grid: horizontalGrid,
      tooltip: {
        ...premiumTooltip,
        formatter(params) {
          const p = Array.isArray(params) ? params[0] : params;
          if (!p) return '';
          return (
            `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name}</div>`
            + `<div style="font-variant-numeric:tabular-nums">Revenue waived: ${getCurrencySymbol()}${formatRupeesIndianCompact(p.value)}</div>`
          );
        },
      },
      xAxis: {
        ...indianValueAxis,
        position: 'bottom',
        axisLabel: {
          ...indianValueAxis.axisLabel,
          formatter: value => `${getCurrencySymbol()}${formatRupeesIndianCompact(value)}`,
        },
      },
      yAxis: {
        ...indianCategoryAxis,
        data: prepared.labels,
        inverse: true,
        axisLabel: {
          ...indianCategoryAxis.axisLabel,
          width: 110,
          overflow: 'truncate',
        },
      },
      series: [
        {
          name: 'Revenue Waived',
          type: 'bar',
          data: prepared.values,
          barWidth: '55%',
          itemStyle: {
            color: TEAL,
            borderRadius: [0, 3, 3, 0],
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0,0,0,0.18)',
              color: '#26A69A',
            },
          },
        },
      ],
      ...defaultAnimation,
    }),
    [prepared],
  );

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || 'Waived revenue from complimentary events for the selected date range.';

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        columns={3}
        items={
          apiKpis || [
            { label: 'Complimentary Events', value: '—' },
            { label: 'Revenue Waived', value: '—' },
            { label: '% of Total Revenue', value: '—' },
          ]
        }
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={{ width: '100%', height: plotHeight }}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default ComplimentaryRevenueByVenueChart;
